import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import {
  Header2,
  Text,
  Input,
  LinkButton,
  SectionHeader
} from '../styles/Components'
import Button from '../components/Button'
import styled from '@emotion/styled'
import { gsColors } from '../styles/Variables'
import { mq } from '../helpers'
import { FaFacebookF, FaTwitter, FaRegEnvelope } from 'react-icons/fa'
import { orderBy } from 'lodash'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useUserContext } from '../components/UserContext'
import { getAccessToken } from '../services'
import LoadingStep from '../components/book/steps/components/LoadingStep'

const INVITE_TWEET =
  '%40GlamSquad brings you hair%2C makeup and nails services on demand. Sign up to get %2420 off your first appointment%21%0A'
const EMAIL_SUBJECT = 'Here%E2%80%99s %2420 to use on Glamsquad.'
const EMAIL_BODY =
  'On Glamsquad you can book hair%2C makeup%2C and nail services with talented Beauty Professionals that come to you anytime%2C anywhere. Sign up with this link and get %2425 off your first appointment'

const InviteFriends = () => {
  const { user } = useUserContext()
  const ref = useRef()
  const router = useRouter()

  const isLoggedIn = !!getAccessToken()

  useEffect(() => {
    if (!isLoggedIn) {
      router.push('/signin?onSuccess=/invite-friends')
    }
  }, [isLoggedIn, router])

  useEffect(() => {
    if (user && user.referralUrl) {
      ref.current.value = user.referralUrl
    }
  }, [user])

  const [copied, setCopied] = useState(false)

  const referralToClipboard = () => {
    if (navigator) {
      navigator.clipboard.writeText(user.referralUrl)
    }
    setCopied(true)
  }

  return (
    <InviteFriendsWrapper>
      <Head>
        <title>Invite Friends | Glamsquad</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {user && user.id ? (
        <div className="invite-friends-content">
          <div className="invite-header">
            <Header2 align="center">Invite Friends & Earn Credits!</Header2>
            <Text margin="25px 0">
              Refer a friend, and get $20 in credit when they complete their
              first appointment. When a new Glamsquad user signs up through your
              link, they will get $20 off their first appointment.
            </Text>
          </div>

          <div className="terms-container">
            <Text weight={700} variant="small">
              SHARE YOUR PERSONAL LINK
            </Text>
            <Link href='/terms'>
              Terms & Conditions
            </Link>

          </div>

          <div className="invite-copy">
            <span>YOUR PERSONAL CODE:</span>
            <Input
              disabled={true}
              placeholder="code here"
              name="referral-code"
              ref={ref}
            />

            <Button
              outline={!copied}
              color={gsColors.sunsetOrange}
              onClick={referralToClipboard}
              id="main-content"
            >
              {copied ? 'Copied!' : 'copy'}
            </Button>
          </div>

          <div className="invite-separators">
            <div />
            <Text margin="0 5px" color={gsColors.saltBox} variant="small">
              OR
            </Text>
            <div />
          </div>

          <div className="invite-social">
            <LinkButton
              target="_blank"
              href={`mailto:?subject=${EMAIL_SUBJECT}&body=${EMAIL_BODY} ${user.referralUrl}`}
              color={gsColors.sunsetOrange}
            >
              <FaRegEnvelope /> Email
            </LinkButton>
            <LinkButton
              target="_blank"
              href={`https://www.facebook.com/sharer/sharer.php?u=${user.referralUrl}`}
              color="#3b5998"
            >
              <FaFacebookF /> Facebook
            </LinkButton>
            <LinkButton
              target="_blank"
              href={`https://twitter.com/intent/tweet?text=${INVITE_TWEET}&url=${user.referralUrl}`}
              color="#1DA1F2"
            >
              <FaTwitter /> Twitter
            </LinkButton>
          </div>

          <Text margin="30px 0">
            You have&nbsp;
            <span style={{ color: gsColors.sunsetOrange }}>
              ${user.credits}
            </span>{' '}
            in credits!
          </Text>

          <SectionHeader small margin="10px 0 20px 0">
            Your Invites
          </SectionHeader>

          <div className="referred-users">
            <ul>
              {user.referrals && user.referrals.length ? (
                orderBy(user.referrals, 'referrerRewarded', 'desc').map(r => (
                  <li key={r.id}>
                    <div className="referred-row">
                      <Text color={gsColors.honeyFlower} variant="small">
                        {r.recipient.email}&nbsp;
                        <span style={{ color: gsColors.saltBox }}>
                          {r.referrerRewarded
                            ? 'completed their first appointment.'
                            : 'signed up, but hasn\'t completed an appointment yet.'}
                        </span>
                      </Text>

                      <Text
                        color={
                          r.referrerRewarded
                            ? gsColors.sunsetOrange
                            : gsColors.saltBox
                        }
                        variant="small"
                      >
                        ${r.rewardAmount}&nbsp;
                        {r.referrerRewarded ? 'Earned' : 'Pending'}
                      </Text>
                    </div>
                  </li>
                ))
              ) : (
                <div className="no-referrals">
                  <div className="box">
                    <Text variant="small" color={gsColors.saltBox}>
                      Looks like you haven&apos;t invited anyone yet. Choose an
                      option above to start inviting friends!
                    </Text>
                  </div>
                </div>
              )}
            </ul>
          </div>
        </div>
      ) : (
        <LoadingStep />
      )}
    </InviteFriendsWrapper>
  )
}

export default InviteFriends

export const InviteFriendsWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 4rem;
  height: 100%;
  background-color: ${gsColors.coral};

  .referred-users {
    width: 100%;

    .no-referrals {
      .box {
        text-align: center;
        border: 2px solid ${gsColors.ebb};
        padding: 20px 50px;
      }
    }

    .referred-row {
      display: flex;
      margin-bottom: 20px;
      gap: 10px;

      p:nth-of-type(1) {
        flex: 1;
        text-overflow: ellipsis;
        overflow: auto;
      }

      p:nth-of-type(2) {
        text-align: right;
      }

      ${mq('max', '500px')} {
        justify-content: center;

        p:nth-of-type(1) {
          span {
            display: none;
          }
        }
      }
    }
  }

  .invite-friends-content {
    max-width: 600px;
    margin-top: 140px;
    padding: 0 40px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .invite-social {
    gap: 5px;
    width: 100%;
    display: flex;
    margin: 10px 0;
    align-items: center;

    ${mq('max', '586px')} {
      flex-direction: column;
    }

    a {
      max-width: 260px;
      min-width: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: calc(100% / 3);

      ${mq('max', '586px')} {
        width: 100%;
        max-width: unset;
      }
    }

    svg {
      font-size: 22px;
      margin: 0 5px;
      vertical-align: middle;
    }
  }

  .invite-seperators {
    width: 100%;
    display: flex;

    div {
      margin: auto;
      width: 45%;
      background-color: #f4f0f0;
      height: 1px;
    }
  }

  .terms-container {
    margin: 15px;
    text-align: center;
    a {
      font-size: 0.8em;
      text-transform: capitalize;
      padding: 5px;
      text-decoration: none;
    }
  }

  .invite-copy {
    text-align: center;
    position: relative;
    display: flex;
    margin: 10px 0;

    ${mq('max', '768px')} {
      justify-content: center;
      width: 100%;
    }

    span {
      position: absolute;
      font-size: 12px;
      color: ${gsColors.honeyFlower};
      font-weight: 700;
      bottom: 5px;

      ${mq('max', '768px')} {
        display: none;
      }
    }

    button {
      min-width: min-content;
      margin: 0 15px;
      padding: 8px 10px;
      font-size: 12px;
    }

    input {
      font-size: 12px;
      max-width: 400px;
      width: 400px;
      padding-left: 150px;
      ${mq('max', '768px')} {
        max-width: 250px;
        width: 100%;
        padding-left: 0;
      }
    }
  }
`
